import { useState } from 'react';
import { Alert, Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Icon, IconButton, Input, Typography } from '@sm-dashboard/ui-components';
import { Ai } from '@/assets/icons/Ai';
import { MOBILE_BOTTOM_NAV_INSET } from '@/layouts/mobileLayoutConstants';
import { useCrmAiChat } from './useCrmAiChat';
import ChatMessageList from './ChatMessageList';
import ChatSuggestions from './ChatSuggestions';

interface CrmAiChatPanelProps {
  open: boolean;
  onClose: () => void;
}

const CrmAiChatPanel = ({ open, onClose }: CrmAiChatPanelProps) => {
  const [draft, setDraft] = useState('');
  const { messages, isStreaming, activeTool, error, send, reset, approveAction, rejectAction } = useCrmAiChat();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleSend = () => {
    if (!draft.trim() || isStreaming) return;
    send(draft);
    setDraft('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        // Above the sidebar HoverMenu (1300) so the open chat isn't covered next to the sidebar.
        zIndex: theme.zIndex.modal + 50,
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '20px',
        ...(isSmallScreen
          ? {
              left: 12,
              right: 12,
              bottom: MOBILE_BOTTOM_NAV_INSET + 12,
              height: 'min(70vh, 560px)'
            }
          : {
              // Mirrored by the RTL stylis plugin — renders on the visual right, above the bubble,
              // clear of the sidebar.
              left: 110,
              bottom: 96,
              width: 380,
              height: 540
            })
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Ai />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" color="text.primary">
            עוזר AI
          </Typography>
        </Box>
        {messages.length > 0 && (
          <Button variant="outlined" size="small" onClick={reset} startIcon={<RefreshIcon />}>
            שיחה חדשה
          </Button>
        )}
        <IconButton variant="text" size="small" onClick={onClose} aria-label="סגירת הצאט">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {messages.length === 0 ? (
          <ChatSuggestions onSelect={send} />
        ) : (
          <ChatMessageList
            messages={messages}
            isStreaming={isStreaming}
            activeTool={activeTool}
            onApproveAction={approveAction}
            onRejectAction={rejectAction}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Input */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          px: 1.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Input
          appearance="plain"
          multiline
          maxRows={4}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="שאלו אותי משהו..."
          disabled={isStreaming}
          autoFocus
        />
        <IconButton
          shape="circle"
          size="medium"
          onClick={handleSend}
          disabled={!draft.trim() || isStreaming}
          aria-label="שליחה"
        >
          <Icon icon="arrowUpward" color="inherit" size="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default CrmAiChatPanel;
